import React, { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { FAQS, FAQS_BY_SLUG } from "../../data/faqs";
import { logAnalyticsEvent } from "../../Firebase";
import useSeo from "../../hooks/useSeo";
import "./Public.css";

export default function FaqDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const faq = FAQS_BY_SLUG[slug];

  const relatedFaqs = useMemo(
    () => FAQS.filter((item) => item.slug !== slug).slice(0, 3),
    [slug]
  );

  useSeo({
    title: faq
      ? `${faq.question} | Crowned Roofing FAQ`
      : "Roofing FAQ | Crowned Roofing",
    description:
      faq?.description ||
      "Detailed roofing answers for Ontario property owners from Crowned Roofing.",
    ogImage: "https://www.crownedroofing.com/images/ogimg.png",
    canonical: `https://www.crownedroofing.com/faq/${slug}`,
  });

  if (!faq) {
    return (
      <div className="faqPage">
        <Header />
        <section className="faqMissing">
          <div className="faqContainer">
            <h1>FAQ not found</h1>
            <p>The page you requested may have moved. Browse the full FAQ library instead.</p>
            <button type="button" className="faqPrimaryBtn" onClick={() => navigate("/faq")}>
              Back to FAQ
            </button>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="faqPage">
      <Header />

      <section className="faqDetailHero">
        <div className="faqContainer">
          <button type="button" className="faqBackBtn" onClick={() => navigate("/faq")}>
            Back to FAQ
          </button>

          <div className="faqDetailCard">
            <div className="faqCardCategory">{faq.category}</div>
            <h1>{faq.heroTitle}</h1>
            <p className="faqLead">{faq.intro}</p>
          </div>
        </div>
      </section>

      <section className="faqDetailBody">
        <div className="faqContainer faqDetailGrid">
          <article className="faqArticle">
            {faq.sections.map((section) => (
              <section key={section.heading} className="faqArticleSection">
                <h2>{section.heading}</h2>
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </section>
            ))}

            <section className="faqArticleSection">
              <h2>What to Keep in Mind</h2>
              <ul className="faqBulletList">
                {faq.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </section>

            <section className="faqTakeaway">
              <h2>Bottom Line</h2>
              <p>{faq.takeaway}</p>
            </section>
          </article>

          <aside className="faqSidebar">
            <div className="faqSidebarCard">
              <div className="faqSidebarTitle">Need an answer for your roof specifically?</div>
              <p>
                General guidance helps, but the right decision still depends on roof condition,
                ventilation, detailing, and access.
              </p>
              <button
                type="button"
                className="faqPrimaryBtn"
                onClick={() => {
                  logAnalyticsEvent("faq_contact_click", {
                    slug: faq.slug,
                    source: "faq_detail",
                  });
                  navigate("/contact");
                }}
              >
                Request an Assessment
              </button>
            </div>

            <div className="faqSidebarCard">
              <div className="faqSidebarTitle">Related Questions</div>
              <div className="faqRelatedList">
                {relatedFaqs.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/faq/${item.slug}`}
                    className="faqRelatedLink"
                    onClick={() =>
                      logAnalyticsEvent("faq_related_open", {
                        slug: item.slug,
                        source: faq.slug,
                      })
                    }
                  >
                    {item.question}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </div>
  );
}
